import localforage from 'localforage'
import {
  consumeLoginPopupAbandoned,
  LOGIN_POPUP_REDIRECT_KEY,
  type LoginPopupRedirectRecord
} from '@/utils/login-popup-tracking'
import {
  emitter,
  FIREBASE_INITIALIZED,
  AUTH_STATE_CHANGED
} from '@/plugins/emitter'
import {
  getFirebaseAuth,
  onAuthStateChanged,
  getRedirectResult,
  isSignInWithEmailLink,
  signInWithEmailLink
} from '@/infra/firebase/auth'
import { withCompatAuthUserMethods } from '@/utils/auth/compatUserMethods'
import { readEmailChangePending } from '@/utils/auth/emailChangePending'
import { LOGIN_POPUP_REDIRECT_TTL_MS } from './constants'
import { buildVarunaLoginFormData, postVarunaLogin } from './formDataHelpers'

declare const TokenInvoker: { postMessage: (uid: string) => void }
declare const window: Window & {
  WhatapBrowserAgent: { setUserID: (uid: string) => void }
  prompt: typeof globalThis.prompt
}

/**
 * Google/AppleID/LINEなどリダイレクト系ログインの成功を、
 * 退避しておいた計測コンテキストから回収する。
 */
export function consumePendingLoginPopupRedirect(main: any): void {
  localforage.getItem(
    LOGIN_POPUP_REDIRECT_KEY,
    function (err: unknown, pending: LoginPopupRedirectRecord | null) {
      if (err || !pending) {
        return
      }
      localforage.removeItem(LOGIN_POPUP_REDIRECT_KEY)
      if (Date.now() - pending.ts > LOGIN_POPUP_REDIRECT_TTL_MS) {
        return
      }
      main.$gtm.sendLoginPopupEvent('login_success', {
        format_id: pending.formatId,
        trigger_button: pending.triggerButton
      })
      // リダイレクト系ログインは遷移時に必ず hidden を通るため、この経路の成功は
      // ほぼ全件が hidden で計上済みの放棄を持つ。打ち消しを送って過大計上を防ぐ。
      const recoveredFrom = consumeLoginPopupAbandoned()
      if (recoveredFrom) {
        main.$gtm.sendLoginPopupEvent('abandon_recovered', {
          format_id: pending.formatId,
          trigger_button: pending.triggerButton,
          abandon_reason: recoveredFrom
        })
      }
    }
  )
}

function handleRedirectLoginSuccess(main: any, result: any): void {
  if (result.user.email) {
    main.showResultMessage(
      'ログインしました。：' + result.user.email,
      'success'
    )
    console.error(9999)
    main.isLoginAction = true
    console.error(main.isLoginAction)
    const formData = buildVarunaLoginFormData(main, result.user, {
      includeConfirmCode: true
    })
    postVarunaLogin(main, formData)
    main.handleDigramExamId(result.user)
  } else {
    main.showResultMessage('ログインしました。', 'success')
    main.isLoginAction = true
    const formData = buildVarunaLoginFormData(main, result.user, {
      email: '',
      includeConfirmCode: true
    })
    postVarunaLogin(main, formData)
    main.handleDigramExamId(result.user)
  }
}

/**
 * OAuth redirect result → Varuna login。
 * modular では pending 無しのとき null。成功時は UserCredential（credential プロパティは無い）。
 */
export async function wireRedirectResult(main: any): Promise<void> {
  try {
    const result = await getRedirectResult(getFirebaseAuth())
    console.error('getRedirectResult')
    console.error(result)
    if (!result || !result.user) return
    main.user = withCompatAuthUserMethods(result.user)
    if (readEmailChangePending()) return
    handleRedirectLoginSuccess(main, result)
  } catch (error: any) {
    console.error('getRedirectResult failed', error)
    main.showResultMessage(
      'ログインに失敗しました：' + (error?.message || error),
      'error'
    )
  }
}

/**
 * Email-link fallback（router.beforeEach ですり抜けた場合のみ）。
 * 既に currentUser がいるなら重複実行を防ぐ。
 */
export function wireEmailLinkFallback(main: any): void {
  const auth = getFirebaseAuth()
  if (!isSignInWithEmailLink(auth, window.location.href) || auth.currentUser) {
    return
  }
  localforage.getItem<string>('emailForSignIn').then(function (
    email: string | null
  ) {
    if (!email) {
      email = window.prompt(
        '別の端末でログインする場合、安全のため再度メールアドレスを入力してください。\n※一度使用したメールリンクは無効となります'
      )
    }
    if (main.user && main.user.email) {
      main.showResultMessage('すでにログイン済みです')
      return
    }
    if (!email) {
      main.showResultMessage('再度ログインし直してください')
      return
    }
    main.$gtm.hintAuthMethod('email_link')
    signInWithEmailLink(auth, email, window.location.href)
      .then(function (result) {
        console.error('signInWithEmailLink')
        main.showResultMessage('ログインしました：' + result.user.email)
        main.isLoginAction = true
        localforage.removeItem('emailForSignIn')
        const formData = buildVarunaLoginFormData(main, result.user, {
          includeConfirmCode: true
        })
        postVarunaLogin(main, formData)
        main.handleDigramExamId(result.user)
      })
      .catch(function (error: unknown) {
        console.error(error)
        main.$gtm.clearAuthMethodHint()
        main.showResultMessage(
          'ログインに失敗しました。メールリンクを再度送信してください。',
          'error'
        )
      })
  })
}

/** Primary auth state listener + session / GTM / events */
function wireOnAuthStateChanged(main: any): void {
  onAuthStateChanged(getFirebaseAuth(), function (user) {
    console.error('firebase-lib onAuthStateChanged 1134')
    main.user = withCompatAuthUserMethods(user)
    console.error(user)
    if (user) {
      console.error(user)
      try {
        TokenInvoker.postMessage(user.uid)
      } catch (error: any) {
        console.warn(
          `Error occurred while calling postMessage: ${error.message}`
        )
      }
      const rescueFormData = new FormData()
      rescueFormData.append('uuid', user.uid)
      rescueFormData.append('email', user.email || '')
      main.$axios
        .post(main.$varuna.endPoints.ensureMember, rescueFormData, {
          timeout: 5000
        })
        .catch(function (err: unknown) {
          console.warn('ensure_member rescue failed', err)
        })
      main.init()
      main.$gtm.sendAuthEvent(user)
      consumePendingLoginPopupRedirect(main)
      emitter.emit(FIREBASE_INITIALIZED, user)
      main.$cookies.config(0, '')
      main.$cookies.set(
        '_YAGISH_SESSION_KEY',
        main.user.uid,
        null,
        null,
        'yagish.jp'
      )
      emitter.emit(AUTH_STATE_CHANGED, main.user)
      window.WhatapBrowserAgent.setUserID(main.user.uid)
    } else {
      emitter.emit(FIREBASE_INITIALIZED, user)
      main.deactivateAutoBackup()
    }
  })
}

/** Resolves once on first auth state emission */
function createAuthReady(main: any): Promise<any> {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(getFirebaseAuth(), (user) => {
      main.user = withCompatAuthUserMethods(user)
      unsub()
      resolve(user)
    })
  })
}

/**
 * Wire all install-time auth lifecycle listeners.
 * redirect 結果の回収を authReady より先に完了させ、SSO 戻りを取りこぼさない。
 */
export function wireAuthLifecycle(main: any): void {
  main.authReady = (async () => {
    await wireRedirectResult(main)
    wireEmailLinkFallback(main)
    wireOnAuthStateChanged(main)
    return createAuthReady(main)
  })()
}
